import type { MarketIdentifiers } from '$lib/types/market';
import { prepareWriteContract, writeContract, type SendTransactionResult, fetchFeeData } from '@wagmi/core';
import abi from '$lib/web3/abi';
import { ZERO_ADDRESS } from '$lib/web3/addresses';
import type { UserMargin } from '$lib/types/user';
import { trade } from '../connectors/trade';
import { estimateGasForConnectors, getMaxFeeAndPriorityFee } from '../utils/gas';
import { buildMarginCallData } from '../builders/margin';
import { buildExpiredOrderCallData } from '../builders/orders';
import type { Order } from '$lib/types/order';
import {
	buildPairOrderCallData,
	buildCancelPairOrderCallData,
	buildCancelFullOrderCallData
} from '../builders/advancedOrders';
import {
	buildCancelTradeSmartContract,
	buildCloseFullTradeSmartContract
} from '../builders/tradeStandard';
import { estimateGasLimits } from '../estimateGaslimit';
import { updateKeeperFee } from '../connectors/fees';
import { isItBedRockShutDownTime, throwError } from '$lib/home/util';
import type { IMatchaResponse } from '../types';
import { buildSwapMatchaCalldata } from '../builders/swap';
import BigNumber from 'bignumber.js';
import type { Token, TokenSymbol } from '$lib/types/tokens';
import { ethers } from 'ethers';
import { MAX_PRIORITY_FEE_PER_GAS } from '../contants';

export async function executeStandardOrder(
	marginDelta: string,
	sizeDelta: string,
	expiry: string,
	marketIdentifier: MarketIdentifiers,
	priceImpact: string,
	marginsWithoutPositions: Array<UserMargin>,
	tokensAllowedForDepositBalance: Record<string, Token>,
	expiredOrders: Array<Order>,
	takeProfitInput: string,
	stopLossInput: string,
	isEntirePosition: boolean,
	smartContractWalletAddress: `0x${string}`,
	AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION: boolean,
	shouldUpdateKeeperFee: boolean,
	amountPerTokenToSwap: Record<string, BigNumber>,
	matchaCallData: Record<string, IMatchaResponse>,
	AUTOSWAP_STABLES: boolean,
	AUTOSWAP_OP: boolean,
	tokensAllowedForDeposit: Array<TokenSymbol>
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callData: Array<string> = [];

	const cancelExpiredOrdersCallData = await buildExpiredOrderCallData(expiredOrders);
	targetArray.push(...cancelExpiredOrdersCallData._targetArray);
	callData.push(...cancelExpiredOrdersCallData._callDataArray);

	const isAddMaxMargin = marginDelta === ethers.constants.MaxUint256.toString();
	if (AUTOSWAP_STABLES || AUTOSWAP_OP) {
		const swapCallData = await buildSwapMatchaCalldata(
			tokensAllowedForDeposit,
			tokensAllowedForDepositBalance,
			amountPerTokenToSwap,
			matchaCallData,
			marketIdentifier,
			isAddMaxMargin
		);
		targetArray.push(...swapCallData._targetArray);
		callData.push(...swapCallData._callDataArray);

		if (swapCallData._changedMargin && new BigNumber(marginDelta).gt(0) && !isAddMaxMargin) {
			if (new BigNumber(marginDelta).minus(swapCallData._changedMargin as BigNumber).gt(0)) {
				marginDelta = new BigNumber(marginDelta)
					.minus(swapCallData._changedMargin as BigNumber)
					.toFixed(0);
			} else {
				marginDelta = '0';
			}
		}
	}

	// build margin for sUSD that was not swapped
	const marginCallData = buildMarginCallData(
		marginsWithoutPositions,
		marketIdentifier,
		marginDelta,
		AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION
	);
	targetArray.push(...marginCallData._targetArray);
	callData.push(...marginCallData._callData);

	if (shouldUpdateKeeperFee) {
		const updateKeeperFeeCallData = updateKeeperFee();
		targetArray.push(updateKeeperFeeCallData._target);
		callData.push(updateKeeperFeeCallData._callData);
	}

	const { _target, _callData } = await trade(marketIdentifier, sizeDelta, priceImpact);
	targetArray.push(_target);
	callData.push(_callData);

	const pairOrderCallData = buildPairOrderCallData(
		marketIdentifier,
		sizeDelta,
		isEntirePosition,
		priceImpact,
		expiry,
		takeProfitInput,
		stopLossInput
	);
	targetArray.push(...pairOrderCallData._targetArray);
	callData.push(...pairOrderCallData._callData);

	let gasLimit = await estimateGasForConnectors(
		_smartContractWalletAddress,
		targetArray,
		callData,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();



	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0xstring`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callData, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function executePairOrder(
	marketIdentifier: MarketIdentifiers,
	sizeDelta: string,
	isEntirePosition: boolean,
	priceImpact: string,
	expiry: string,
	takeProfitInput: string,
	stopLossInput: string,
	smartContractWalletAddress: `0x${string}`
) {
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callData: Array<string> = [];

	const pairOrderCallData = buildPairOrderCallData(
		marketIdentifier,
		sizeDelta,
		isEntirePosition,
		priceImpact,
		expiry,
		takeProfitInput,
		stopLossInput
	);
	targetArray.push(...pairOrderCallData._targetArray);
	callData.push(...pairOrderCallData._callData);

	let gasLimit = await estimateGasForConnectors(
		_smartContractWalletAddress,
		targetArray,
		callData,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();



	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0xstring`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callData, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function editPairOrder(
	orderId: string,
	marketIdentifier: MarketIdentifiers,
	sizeDelta: string,
	isEntirePosition: boolean,
	priceImpact: string,
	expiry: string,
	takeProfitInput: string,
	stopLossInput: string,
	smartContractWalletAddress: `0x${string}`
) {
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callData: Array<string> = [];

	const cancelPairOrderCallData = await buildCancelFullOrderCallData(orderId);

	targetArray.push(...cancelPairOrderCallData._targetArray);
	callData.push(...cancelPairOrderCallData._callDataArray);

	const pairOrderCallData = buildPairOrderCallData(
		marketIdentifier,
		sizeDelta,
		isEntirePosition,
		priceImpact,
		expiry,
		takeProfitInput,
		stopLossInput
	);
	targetArray.push(...pairOrderCallData._targetArray);
	callData.push(...pairOrderCallData._callData);

	let gasLimit = await estimateGasForConnectors(
		_smartContractWalletAddress,
		targetArray,
		callData,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();



	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0xstring`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callData, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function cancelTradeSmartContract(
	marketIdentifier: MarketIdentifiers,
	smartContractWalletAddress: `0x${string}`
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];
	const functionName: string = 'cast';

	const cancelTradeSmartContractCallData = await buildCancelTradeSmartContract(marketIdentifier);

	targetArray.push(...cancelTradeSmartContractCallData._targetArray);
	callDataArray.push(...cancelTradeSmartContractCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();



	const config = await prepareWriteContract({
		address: smartContractWalletAddress,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function closeFullTradeSmartContract(
	marketIdentifier: MarketIdentifiers,
	priceImpact: string,
	smartContractWalletAddress: `0x${string}`,
	shouldUpdateKeeperFee: boolean
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];
	const functionName: string = 'cast';

	if (shouldUpdateKeeperFee) {
		const updateKeeperFeeCallData = updateKeeperFee();
		targetArray.push(updateKeeperFeeCallData._target);
		callDataArray.push(updateKeeperFeeCallData._callData);
	}

	const closeFullTradeSmartContractCallData = await buildCloseFullTradeSmartContract(
		marketIdentifier,
		priceImpact
	);

	targetArray.push(...closeFullTradeSmartContractCallData._targetArray);
	callDataArray.push(...closeFullTradeSmartContractCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();



	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});

	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function cancelPairOrderSmartContract(
	orderId: string,
	smartContractWalletAddress: `0x${string}`
) {
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];
	const functionName: string = 'cast';

	const cancelPairOrderSmartContractCallData = await buildCancelPairOrderCallData(orderId);

	targetArray.push(...cancelPairOrderSmartContractCallData._targetArray);
	callDataArray.push(...cancelPairOrderSmartContractCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();



	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});

	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function closeAllTradeSmartContract(
	marketIdentifiers: MarketIdentifiers[],
	priceImpact: string,
	smartContractWalletAddress: `0x${string}`,
	shouldUpdateKeeperFee: boolean
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];
	const functionName: string = 'cast';

	if (shouldUpdateKeeperFee) {
		const updateKeeperFeeCallData = updateKeeperFee();
		targetArray.push(updateKeeperFeeCallData._target);
		callDataArray.push(updateKeeperFeeCallData._callData);
	}

	for (const marketIdentifier of marketIdentifiers) {
		const closeFullTradeSmartContractCallData = await buildCloseFullTradeSmartContract(
			marketIdentifier,
			priceImpact
		);
		targetArray.push(...closeFullTradeSmartContractCallData._targetArray);
		callDataArray.push(...closeFullTradeSmartContractCallData._callDataArray);
	}

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();



	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});

	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}
